import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * 평상시 색 → hover 색. Tailwind 정적 스캔 때문에 완성형 리터럴 맵으로 둔다.
 *
 * 실측(2026-07-28 apps/web 순수 텍스트 액션 90곳)에서 최빈 쌍이 `brand-600 → brand-800`(30곳)이라
 * **명도 +200 램프**를 세 tone에 일관 적용했다. 나머지는 같은 뜻인데 값만 갈린 드리프트다
 * (`brand-600→500`(역방향 10곳)·`red-400→600` 8·`red-600→800` 7·`ink-500→700` 2 …).
 */
const TONE_CLASS = {
  /** 기본 — "추가"·"편집" 등 보조 액션(실측 46곳으로 최다). */
  brand: 'text-brand-600 hover:text-brand-800',
  /** 삭제·제거 등 파괴적 텍스트 액션. */
  danger: 'text-rose-600 hover:text-rose-800',
  /** 저강도 — "취소"·"닫기" 등. */
  neutral: 'text-ink-600 hover:text-ink-800',
} as const;

export type TextButtonTone = keyof typeof TONE_CLASS;

export interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** 의미 색. 기본 brand. */
  tone?: TextButtonTone;
  children?: ReactNode;
  /** 글자 크기·굵기·정렬 등. 아래 주석대로 크기/굵기는 이쪽으로 넘긴다. */
  className?: string;
}

/**
 * 박스 없는 인라인 텍스트 액션 — "추가"·"편집"처럼 글자만 있는 버튼.
 *
 * **`Button`과 분리한 이유**: `Button`의 불변식은 *고정 높이를 가진 박스*(`h-*`·`px-*`·
 * `border`·radius·ring offset)인데 텍스트 액션은 박스가 없어야 한다. `variant="link"`로
 * 욱여넣으면 `size`·`iconOnly`가 조용히 무의미해지는 prop이 되어 API가 거짓말을 한다
 * (`Modal`/`ConfirmModal`을 한 컴포넌트의 플래그로 합치지 않은 것과 같은 이유).
 *
 * **의도적으로 시각을 바꾸지 않는다**: 시안(목업 9종)에는 텍스트 링크형 액션이라는 개념이
 * 아예 없고(액션은 `.btn` 박스 또는 `.row-actions .ico` 정사각 둘뿐), 이들이 있는 자리는
 * 대부분 **시안이 없는 화면**(모달·상세 내부)이다. 근거 없이 38px 박스로 승격하면 인라인
 * 흐름이 벌어지므로, 지금은 **흩어진 변종을 한 출처로 모으기만** 한다. 그 화면들의 시안이
 * 정해지면 이 파일 한 곳만 고치면 된다 — 프리미티브를 두는 이유가 그것이다.
 *
 * 보장하는 것: `type="button"` 기본값 · disabled 시각/커서 · 포커스 링 · tone 색 쌍.
 * 일부러 정하지 않는 것: **글자 크기와 굵기**. 실측이 상속 31 / `text-xs` 30 / `text-sm` 26,
 * 굵기는 미지정 72 / `font-medium` 18로 갈려 있어, 하나를 박으면 이관이 곧 시각 변경이 된다.
 * 호출부가 `className`으로 넘긴다(현행 값을 그대로 옮기면 무변화).
 *
 * 밑줄(`underline`)이 붙은 것은 이 갈래가 아니다 — 링크 시맨틱이므로 `<a>`/`<Link>`로 둔다.
 */
export function TextButton({
  tone = 'brand',
  type = 'button',
  className = '',
  children,
  ...rest
}: TextButtonProps) {
  return (
    <button
      type={type}
      className={`rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 ${TONE_CLASS[tone]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
